<script setup lang="ts">
import LargeDialog from './LargeDialog.vue'
import { areaPortraitFor } from '~/composables/useSceneImage'

/**
 * 冒険の手記の一覧（#900）。
 *
 * エリアをクリアすると「物語の 1 ページ」が増える。ここは**額縁が並ぶ棚**で、
 * 選ぶとクリア時と同じページをもう一度読める（本文は再読イベントだけが持つ）。
 *
 * **未獲得のページは、タイトルも挿絵もサーバから送られてこない**（`build_memoir_state`）。
 * ここで出しているのは「まだ手に入れていない何かがある」ことだけ ——
 * CSS で隠すのは開発者ツールで読めるので「伏せた」ことにならない（#900）。
 */
const dialog = defineModel<boolean>()

const gameStateStore = useGameStateStore()
const { memoirs } = storeToRefs(gameStateStore)
const { reloadGameState } = gameStateStore
const { triggerEventAndProcess } = useEventSystem()

const items = computed(() => memoirs.value?.items ?? [])

/** 額に載せる経験値（#971。2026-10-07 doi:「10/60 EXP獲得済」= 得た分 / そのエリアでもらえる分） */
const expText = (earned: number, total: number) => `${earned}/${total} EXP獲得済`

/**
 * 棚の額に入れる絵は**エリアのマップ画像**（`useSceneImage` の AreaMap サムネ）。
 *
 * 手記は**エリアに入った時点で開ける**ようになり、中身はワンダーをクリアするたびに
 * 増えていく。一方 story の挿絵はエリアの物語そのものなので、**出るタイミングが違う**。
 * そこで棚には「どのエリアの手記か」が一目で分かるマップ画像を置き、挿絵は手記の
 * 中身（物語のページ）で使う（#900 のフィードバック）。
 */
const areaImage = (areaId?: string | null) =>
  areaId ? areaPortraitFor(areaId) : undefined

/**
 * 開いたら NEW 印を下ろして、一覧を取り直す。
 *
 * ⚠️ game_state はイベントのたびに再取得されない（`SceneMap` は mount 時と一部の操作だけ）。
 * 獲得直後に開いたときも最新が出るよう、ここで必ず取り直す（#900）。
 */
watch(dialog, async (open) => {
  if (!open) return
  try {
    await triggerEventAndProcess('CLICK:story_memoir.index')
  }
  catch (e) {
    // NEW 印が下りないだけで、一覧そのものは読める。
    console.error('[MemoirListDialog] failed to clear the NEW mark', e)
  }
  await reloadGameState()
})

/**
 * ページを読み終えたら**棚に戻る**（#904）。
 *
 * そのために**棚は閉じずに開いたまま**にして、本文のダイアログをその上に重ねる。
 * 「閉じて、終わったら開き直す」にしなかったのは、**ページの『この場所へ行く』で
 * 画面が移ったかどうかを、閉じた側から知る手立てが無い**ため ——
 * `runAction` は `closeDialog` でリゾルバを解いた**あと**に `eventId` のイベントを
 * 走らせるので、`triggerEventAndProcess` を待っても遷移はまだ起きていない。
 *
 * 重ねる形なら、遷移したかどうかはルートを見れば分かる（下の watch）。
 */
const onSelect = async (eventId: string | null | undefined) => {
  if (!eventId) return
  try {
    await triggerEventAndProcess(eventId)
  }
  catch (e) {
    console.error('[MemoirListDialog] failed to open the memoir', eventId, e)
  }
}

// ファストトラベルで画面が移ったら棚も畳む（行った先に棚が残らないように）。
const route = useRoute()
watch(() => route.fullPath, () => {
  dialog.value = false
})
</script>

<template>
  <LargeDialog
    v-model="dialog"
    title="冒険の手記"
    :cancel-button="{ title: 'とじる' }"
  >
    <!-- **リード文は置かない**（ゼルダ BotW の「ハイラル図鑑」と同じ型）。
         額が並んでいれば説明は要らない。理由と、他ゲームを調べた結果は
         doc/domain/memoir.md「リード文を置かない」。 -->

    <template #default>
      <div
        class="memoir-shelf"
        data-testid="memoir-shelf"
      >
        <!-- 読めるかどうかは button の disabled で表す。**額の外に文字を置かない** ——
             ラベルを下げると、名前のある枠とない枠で高さが変わって列がずれる。 -->
        <button
          v-for="item in items"
          :key="item.memoirId"
          type="button"
          class="memoir-shelf__item"
          :disabled="!item.acquired"
          :data-testid="item.acquired ? 'memoir-acquired' : 'memoir-locked'"
          @click="onSelect(item.eventId)"
        >
          <MemoirFrame
            :src="areaImage(item.areaId)"
            :alt="item.title ?? ''"
          >
            <!-- まだ見えていないエリアは名前もマップ画像も無いので「？」だけになる
                 （名前自体がネタバレなのでサーバが送ってこない）。 -->
            <template #empty>
              <span class="memoir-shelf__inner memoir-shelf__inner--unknown">？</span>
            </template>

            <!-- エリア名は額の中、絵の上に重ねる。 -->
            <template
              v-if="item.title"
              #caption
            >
              <span class="memoir-shelf__inner">
                {{ item.title }}
                <!-- そのエリアでもらえる経験値と、そのうち得た分（#971） -->
                <span
                  v-if="item.expTotal"
                  class="memoir-shelf__exp"
                  data-testid="memoir-exp"
                >{{ expText(item.expEarned ?? 0, item.expTotal) }}</span>
              </span>
            </template>
          </MemoirFrame>
        </button>
      </div>
    </template>
  </LargeDialog>
</template>

<style scoped>
/* 棚。**全 14 エリアぶんの額を必ず並べる**（未公開のぶんも空の額で置く）。
   横 5 列で、入り切らないぶんは縦にスクロールする（器の本文領域が overflow-y: auto）。
   共有コンポーネントなので dvw ではなく calc(N * var(--u))（responsive_sizing.md）。 */
.memoir-shelf {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: max(12px, calc(1.5 * var(--u)));
  padding: max(4px, calc(0.5 * var(--u)));
  /* 最後の段が器の縁に貼り付かないように。 */
  padding-bottom: max(12px, calc(1.5 * var(--u)));

  /* 棚板。羊皮紙よりわずかに暗いベージュを薄く敷いて、スクロールする領域の
     輪郭を出す（濃くすると額の木と喧嘩するので、うっすらに留める）。 */
  background: rgba(92, 62, 30, 0.14);
  border-radius: max(6px, calc(0.8 * var(--u)));
  /* ラベルは獲得済みにしか無いので、額の上端で揃える。 */
  align-items: start;
}

.memoir-shelf__item {
  display: block;
  width: 100%;
  background: none;
  border: none;
  padding: 0;
  font: inherit;
  color: inherit;
  text-align: center;
  cursor: pointer;
  transition: transform 0.12s ease-out;
}

.memoir-shelf__item:hover,
.memoir-shelf__item:focus-visible {
  transform: translateY(max(-3px, calc(-0.4 * var(--u))));
}

/* まだ手記が無いページ。**額の色は落とさない**（羊皮紙が透けて素材と違う色になる）。
   代わりに**中身（マップ画像）を褪せさせて**、手記があるエリアと一目で見分けられるようにする。 */
.memoir-shelf__item:disabled {
  cursor: default;
  transform: none;
}

.memoir-shelf__item:disabled :deep(.memoir-frame__art) {
  /* **ぼかす**。行ったことのないエリアのマップが読めてしまうとネタバレになるので、
     「何かある」だけが伝わる強さまで潰す。ぼかし量は額の大きさに追従させる
     （固定 px だと小さい額で効きすぎ、大きい額で効かない）。 */
  filter: grayscale(0.9) brightness(1.06) blur(max(3px, calc(0.38 * var(--u))));
  opacity: 0.6;
  /* blur は縁がにじんで外側が透ける。少し拡大して縁を開口の外へ逃がす。 */
  transform: scale(1.1);
}

/* 未獲得は**額の色を落とさない**。中身が「？」なだけで、額は同じ木の額が並ぶ。
   （opacity / grayscale を掛けると、羊皮紙が透けて素材と色が違って見える。実際に
   grayscale(0.6) + opacity(0.55) を掛けていたら「素材と色が違う」と指摘された。） */

/* 額の中に置く文字（エリア名 / 「？」）。**額の外には出さない** —— 下にラベルを
   下げると、名前のある枠とない枠で高さが変わって列がずれる。 */
.memoir-shelf__inner {
  font-size: max(10px, calc(1.3 * var(--u)));
  line-height: 1.3;
}

.memoir-shelf__exp {
  display: block;
  margin-top: max(2px, calc(0.3 * var(--u)));
  font-size: max(9px, calc(1.1 * var(--u)));
  font-variant-numeric: tabular-nums;
}

.memoir-shelf__inner--unknown {
  font-size: max(22px, calc(3.2 * var(--u)));
  color: #3a2f1c;
}

@media (max-width: 768px) {
  .memoir-shelf {
    grid-template-columns: repeat(3, 1fr);
  }
}
</style>
